// ─── Angular Imports ───────────────────────────────────────────────────────────
import { Component, Inject, OnInit, OnDestroy, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

// ─── Angular Material Imports ─────────────────────────────────────────────────
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import { Subject, takeUntil } from 'rxjs';

// ─── Application Imports ───────────────────────────────────────────────────────
import { IBatch } from 'src/app/models/batches/batch';
import { BatchResult } from 'src/app/models/batches/batch-result';
import { ValidationErrors } from 'src/app/models/validationErrors';
import { BatchesService } from 'src/app/services/batches.services';

@Component({
    selector: 'app-batch-update',
    templateUrl: './batch-update.component.html',
    styleUrls: ['./batch-update.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class BatchUpdateComponent implements OnInit, OnDestroy {
  // ─── Observables ─────────────────────────────────────────────────────────────
  private destroy$ = new Subject<void>();

  // ─── State ───────────────────────────────────────────────────────────────────
  batch!: IBatch;
  errorState: boolean = false;
  serverErrorMessage: string = '';
  errorMessages: ValidationErrors[] = [];
  validatorProperties: string[] = [];

  // ─── Form Controls ───────────────────────────────────────────────────────────
  input: FormControl;

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private batchesService: BatchesService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<BatchUpdateComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) private data: IBatch,
  ) {
    this.batch = data;
    this.input = new FormControl('', [Validators.required]);
  }

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadBatchDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Data Fetching Methods ──────────────────────────────────────────────────
  private loadBatchDetails(): void {
    this.batchesService
      .detailsBatch(this.batch?.id || '')
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: BatchResult) => {
          if (result?.batch) {
            if (isDevMode()) {
              console.log('[DEV] [BatchUpdateComponent] batch', result.batch);
            }
            this.batch = result.batch;
          }
        },
        (error) => {
          this.serverErrorMessage =
            'There was an error loading the Products from the server. Please restart the system and try again';
          console.error(error.message);
        },
      );
  }

  // ─── Action Methods ─────────────────────────────────────────────────────────
  updateBatch(): void {
    this.batchesService
      .updateBatch(this.batch)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.snackBar.open('Successfully Updated Product', 'close', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            duration: 2000,
            panelClass: ['success'],
          });
          this.dialogRef.close(true);
        },
        (error) => this.handleUpdateBatchError(error),
      );
  }

  private handleUpdateBatchError(error: any): void {
    this.errorState = true;
    if (error.status === 422) {
      error.error.validationErrors.forEach((validationError: ValidationErrors) => {
        this.errorMessages.push(validationError);
        this.validatorProperties.push(validationError.property);
        this.input.markAllAsTouched();
      });
    }
  }

  validationMessage(property: string): string | undefined {
    return this.errorMessages.find((error) => error.property === property)?.message;
  }
}
